"use client";

import { MenuContextProvider, useMenuProviderContext } from "../__chunks/SJPAIJOW.js";
import "../__chunks/QVEJ36PB.js";
import { usePopoverDisclosure } from "../__chunks/QE62MSCL.js";
import "../__chunks/35RDKJF6.js";
import "../__chunks/RM6VCCZ7.js";
import "../__chunks/NWYOIOBE.js";
import "../__chunks/JOBHIYEN.js";
import { useCompositeTypeahead } from "../__chunks/CWIRMIEN.js";
import "../__chunks/ZHUQ44XA.js";
import { useHovercardAnchor } from "../__chunks/BRBME7U5.js";
import "../__chunks/5VQZOHHZ.js";
import "../__chunks/APGXEJ2Q.js";
import "../__chunks/G4PVPWEW.js";
import { Role } from "../__chunks/5HEBF2AL.js";
import "../__chunks/DPAO6G5R.js";
import "../__chunks/COWZSPJ7.js";
import "../__chunks/CMXOY7UW.js";
import "../__chunks/JDEWEW7S.js";
import "../__chunks/Z4WWVOIF.js";
import "../__chunks/SWN3JYXT.js";
import { createElement, createHook, forwardRef } from "../__chunks/7FZLUSKW.js";
import { useStoreState } from "../__chunks/CTKPPFRS.js";
import { useEvent, useId, useMergeRefs, useWrapElement } from "../__chunks/HNHAUIIY.js";
import "../__chunks/SK3NAZA3.js";
import { __objRest, __spreadProps, __spreadValues } from "../__chunks/3YLGPPWQ.js";

// src/menu/menu-button.tsx
import { getPopupItemRole, getPopupRole } from "@ariakit/core/utils/dom";
import { disabledFromProps, invariant } from "@ariakit/core/utils/misc";
import { useRef } from "react";
import { jsx } from "react/jsx-runtime";
var TagName = "button";
function getInitialFocus(event, dir) {
  var keyMap = {
    ArrowDown: dir === "bottom" || dir === "top" ? "first" : false,
    ArrowUp: dir === "bottom" || dir === "top" ? "last" : false,
    ArrowRight: dir === "right" ? "first" : false,
    ArrowLeft: dir === "left" ? "first" : false
  };
  return keyMap[event.key];
}
function hasActiveItem(items, excludeElement) {
  return !!(items == null ? void 0 : items.some(function (item) {
    if (!item.element) return false;
    if (item.element === excludeElement) return false;
    return item.element.getAttribute("aria-expanded") === "true";
  }));
}
var useMenuButton = createHook(function useMenuButton2(_a) {
  var _b = _a,
    store = _b.store,
    focusable = _b.focusable,
    accessibleWhenDisabled = _b.accessibleWhenDisabled,
    _showOnHover = _b.showOnHover,
    props = __objRest(_b, ["store", "focusable", "accessibleWhenDisabled", "showOnHover"]);
  var context = useMenuProviderContext();
  store = store || context;
  invariant(store, process.env.NODE_ENV !== "production" && "MenuButton must receive a `store` prop or be wrapped in a MenuProvider component.");
  var ref = useRef(null);
  var parentMenu = store.parent;
  var parentMenubar = store.menubar;
  var hasParentMenu = !!parentMenu;
  var parentIsMenubar = !!parentMenubar && !hasParentMenu;
  var disabled = disabledFromProps(props);
  var showMenu = function showMenu() {
    var trigger = ref.current;
    if (!trigger) return;
    store == null ? void 0 : store.setDisclosureElement(trigger);
    store == null ? void 0 : store.setAnchorElement(trigger);
    store == null ? void 0 : store.show();
  };
  var onFocusProp = props.onFocus;
  var onFocus = useEvent(function (event) {
    onFocusProp == null ? void 0 : onFocusProp(event);
    if (disabled) return;
    if (event.defaultPrevented) return;
    store == null ? void 0 : store.setAutoFocusOnShow(false);
    store == null ? void 0 : store.setActiveId(null);
    if (!parentMenubar) return;
    if (!parentIsMenubar) return;
    var _parentMenubar$getSta = parentMenubar.getState(),
      items = _parentMenubar$getSta.items;
    if (hasActiveItem(items, event.currentTarget)) {
      showMenu();
    }
  });
  var dir = useStoreState(store, function (state) {
    return state.placement.split("-")[0];
  });
  var onKeyDownProp = props.onKeyDown;
  var onKeyDown = useEvent(function (event) {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (disabled) return;
    if (event.defaultPrevented) return;
    var initialFocus = getInitialFocus(event, dir);
    if (initialFocus) {
      event.preventDefault();
      showMenu();
      store == null ? void 0 : store.setAutoFocusOnShow(true);
      store == null ? void 0 : store.setInitialFocus(initialFocus);
    }
  });
  var onClickProp = props.onClick;
  var onClick = useEvent(function (event) {
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    if (!store) return;
    var isKeyboardClick = !event.detail;
    var _store$getState = store.getState(),
      open = _store$getState.open;
    if (!open || isKeyboardClick) {
      if (!hasParentMenu || isKeyboardClick) {
        store.setAutoFocusOnShow(true);
      }
      store.setInitialFocus(isKeyboardClick ? "first" : "container");
    }
    if (hasParentMenu) {
      showMenu();
    }
  });
  props = useWrapElement(props, function (element) {
    return /* @__PURE__ */jsx(MenuContextProvider, {
      value: store,
      children: element
    });
  }, [store]);
  if (hasParentMenu) {
    props = __spreadProps(__spreadValues({}, props), {
      render: /* @__PURE__ */jsx(Role.div, {
        render: props.render
      })
    });
  }
  var id = useId(props.id);
  var parentContentElement = useStoreState((parentMenu == null ? void 0 : parentMenu.combobox) || parentMenu, "contentElement");
  var role = hasParentMenu || parentIsMenubar ? getPopupItemRole(parentContentElement, "menuitem") : void 0;
  var contentElement = store.useState("contentElement");
  props = __spreadProps(__spreadValues({
    id: id,
    role: role,
    "aria-haspopup": getPopupRole(contentElement, "menu")
  }, props), {
    ref: useMergeRefs(ref, props.ref),
    onFocus: onFocus,
    onKeyDown: onKeyDown,
    onClick: onClick
  });
  props = useHovercardAnchor(__spreadProps(__spreadValues({
    store: store,
    focusable: focusable,
    accessibleWhenDisabled: accessibleWhenDisabled
  }, props), {
    showOnHover: function showOnHover(event) {
      var getShowOnHover = function getShowOnHover() {
        if (typeof _showOnHover === "function") return _showOnHover(event);
        if (_showOnHover != null) return _showOnHover;
        if (hasParentMenu) return true;
        if (!parentMenubar) return false;
        var _parentMenubar$getSta2 = parentMenubar.getState(),
          items = _parentMenubar$getSta2.items;
        return parentIsMenubar && hasActiveItem(items);
      };
      var canShowOnHover = getShowOnHover();
      if (!canShowOnHover) return false;
      var parent = parentIsMenubar ? parentMenubar : parentMenu;
      if (!parent) return true;
      parent.setActiveId(event.currentTarget.id);
      return true;
    }
  }));
  props = usePopoverDisclosure(__spreadValues({
    store: store,
    toggleOnClick: !hasParentMenu,
    focusable: focusable,
    accessibleWhenDisabled: accessibleWhenDisabled
  }, props));
  props = useCompositeTypeahead(__spreadValues({
    store: store,
    typeahead: parentIsMenubar
  }, props));
  return props;
});
var MenuButton = forwardRef(function MenuButton2(props) {
  var htmlProps = useMenuButton(props);
  return createElement(TagName, htmlProps);
});
export { MenuButton, useMenuButton };